import { InPersonBadge } from '@/components/blocks/badges/InPersonBadge';
import { OnlineBadge } from '@/components/blocks/badges/OnlineBadge';
import { ExternalRedirectButton } from '@/components/blocks/buttons/ExternalRedirectButton';
import { DataCardBadge, type DataBadgeVariant } from '@/components/blocks/DataCard';
import { DateTimeLabel } from '@/components/blocks/DateTimeLabel';
import DotIcon from '@/components/blocks/DotIcon';
import { ImageFallback } from '@/components/blocks/ImageFallback';
import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
} from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  RsvpAttendButton,
  RsvpDialog,
  formatUserRsvpStatusLines,
  isEventAtCapacity,
  isUserRegisteredForEvent,
} from '@/features/EventRsvp';
import { DEFAULT_TIME_ZONE } from '@/constants/default';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import type { Event } from '@mr-website/schemas';
import { CheckCircle2, UserRound } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export interface EventListRowProps {
  event: Event;
  className?: string;
  badgeText?: string;
  badgeVariant?: DataBadgeVariant;
  isPersonalizationPending: boolean;
}

const MAX_VISIBLE_SPEAKERS = 3;

function formatDuration(start: string, end: string): string {
  const minutes = (new Date(end).getTime() - new Date(start).getTime()) / 60000;
  if (minutes < 60) return `${Math.round(minutes)}min`;
  const hours = minutes / 60;
  if (hours < 24) {
    const rounded = Math.round(hours * 2) / 2;
    return rounded === 1 ? '1hr' : `${rounded}hrs`;
  }
  const days = hours / 24;
  const rounded = Math.round(days * 2) / 2;
  return rounded === 1 ? '1 day' : `${rounded} days`;
}

export function EventListRow({
  event,
  className,
  badgeText,
  badgeVariant,
  isPersonalizationPending,
}: EventListRowProps) {
  const {
    id: eventId,
    name: title,
    featured_photo: featuredPhoto,
    date_start: dateStart,
    date_end: dateEnd,
    time_zone: timeZone,
    address,
    is_online: online,
    type: eventType,
    speakers,
    rsvp_count: rsvpCount,
    description,
  } = event;

  const { isAuthenticated } = useAuth();
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [imageErrored, setImageErrored] = useState(false);

  // Reset when the source changes so client-side list re-renders retry the new
  // URL instead of staying stuck on the fallback (mirrors DataCard).
  useEffect(() => setImageErrored(false), [featuredPhoto]);

  const accountCenterUrl = process.env.NEXT_PUBLIC_ACCOUNT_CENTER_URL;
  const duration = formatDuration(dateStart, dateEnd);
  const visibleSpeakers = speakers.slice(0, MAX_VISIBLE_SPEAKERS);
  const overflowSpeakerCount = Math.max(0, speakers.length - MAX_VISIBLE_SPEAKERS);
  const displayRsvp = Math.max(7, rsvpCount);
  const isRegistered = isUserRegisteredForEvent(event);
  const statusLines = event.user_rsvp ? formatUserRsvpStatusLines(event.user_rsvp) : [];
  const eventHref = `/event/${eventId}`;

  function handleRsvpClick() {
    if (!isUserRegisteredForEvent(event) && isEventAtCapacity(event)) return;
    if (!isAuthenticated) {
      const redirect = encodeURIComponent(window.location.href);
      window.location.assign(`${accountCenterUrl}/login?redirect=${redirect}`);
      return;
    }
    setRsvpOpen(true);
  }

  return (
    <article>
      <Card
        className={cn(
          'w-full flex-row gap-0 overflow-hidden rounded-2xl border border-card-border py-0 shadow-sm ring-1 ring-foreground/10',
          className,
        )}
      >
        <Link
          href={eventHref}
          prefetch={true}
          aria-label={title}
          onClick={(e) => e.stopPropagation()}
          className="relative block aspect-[16/9] w-[45%] shrink-0 self-center sm:self-start"
        >
          {featuredPhoto && !imageErrored ? (
            <img
              src={featuredPhoto}
              alt={title}
              className="absolute inset-0 size-full object-cover"
              onError={() => setImageErrored(true)}
            />
          ) : (
            <ImageFallback subTitle={eventType} iconSize={32} className="size-full" />
          )}
          {badgeText ? (
            <div className="absolute top-3 left-3 z-10">
              <DataCardBadge color={badgeVariant}>
                {badgeText.charAt(0).toUpperCase() + badgeText.slice(1)}
              </DataCardBadge>
            </div>
          ) : null}
        </Link>

        <div className="flex min-w-0 flex-1 flex-col gap-2 p-4">
          <div>{online ? <OnlineBadge /> : <InPersonBadge />}</div>

          <h3 className="line-clamp-2 text-data-card-md font-bold leading-snug text-foreground m-0">
            <Link
              href={eventHref}
              prefetch={true}
              onClick={(e) => e.stopPropagation()}
              className="text-inherit no-underline hover:underline"
            >
              {title}
            </Link>
          </h3>

          <div className="flex items-center gap-1 text-muted-foreground">
            <DateTimeLabel isoDatetime={dateStart} timeZone={timeZone || DEFAULT_TIME_ZONE} />
            <DotIcon size={12} className="shrink-0" aria-hidden />
            <span className="text-data-card-xs">{duration}</span>
          </div>

          {!online ? (
            <address className="line-clamp-2 text-data-card-xs text-muted-foreground not-italic">
              {address}
            </address>
          ) : null}

          {description ? (
            <p className="line-clamp-2 text-data-card-xs text-muted-foreground">{description}</p>
          ) : null}

          <div className="flex flex-wrap items-center gap-4">
            {visibleSpeakers.length > 0 ? (
              <div className="flex items-center gap-2">
                <span className="text-data-card-xs font-semibold text-muted-foreground">
                  Speakers
                </span>
                <AvatarGroup>
                  {visibleSpeakers.map((speaker) => (
                    <Avatar key={speaker.name} size="sm">
                      {speaker.headshot_image_url ? (
                        <AvatarImage src={speaker.headshot_image_url} alt={speaker.name} />
                      ) : null}
                      <AvatarFallback>
                        <UserRound className="size-3 text-muted-foreground" />
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {overflowSpeakerCount > 0 ? (
                    <AvatarGroupCount>+{overflowSpeakerCount}</AvatarGroupCount>
                  ) : null}
                </AvatarGroup>
              </div>
            ) : null}

            <div className="flex items-center gap-1.5">
              <span className="size-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
              <span className="text-data-card-xs text-muted-foreground">
                {displayRsvp} others going
              </span>
            </div>
          </div>

          {/* CRM link, RSVP/Details, registration status — depends on
              user-specific data that only resolves client-side after
              hydration; show a skeleton rather than flash the anonymous
              state and then swap it. */}
          {isPersonalizationPending ? (
            <div className="flex flex-1 items-end pt-1">
              <Skeleton className="h-10.5 w-full rounded-lg" />
            </div>
          ) : (
            <>
              {event.crm_link ? (
                <ExternalRedirectButton
                  href={event.crm_link}
                  label="CRM"
                  className="self-start bg-employee-only-button"
                  newTab
                />
              ) : null}

              {event.is_private ? (
                <div className="flex flex-1 items-end gap-2 pt-1">
                  <Button
                    variant="outline"
                    render={<Link href={eventHref} prefetch={true} />}
                    nativeButton={false}
                    className="py-5 text-data-card-xs font-bold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Details
                  </Button>
                </div>
              ) : isRegistered ? (
                <div className="flex flex-1 items-end justify-between gap-2 pt-1">
                  <div className="space-y-0.5">
                    {statusLines.map((line) => (
                      <div
                        key={line}
                        className="flex items-center gap-1 text-data-card-xs text-emerald-700"
                      >
                        <CheckCircle2 className="size-3.5 shrink-0" aria-hidden />
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    render={<Link href={eventHref} prefetch={true} />}
                    nativeButton={false}
                    className="shrink-0 py-5 px-4 text-data-card-xs font-bold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Details
                  </Button>
                </div>
              ) : (
                <div className="flex flex-1 items-end gap-2 pt-1">
                  <RsvpAttendButton
                    event={event}
                    isAuthenticated={isAuthenticated}
                    className="py-5 text-data-card-xs font-bold text-white"
                    style={{ backgroundColor: 'var(--color-mr-teal-dark)' }}
                    onClick={(clickEvent) => {
                      clickEvent.stopPropagation();
                      handleRsvpClick();
                    }}
                  />
                  <Button
                    variant="outline"
                    render={<Link href={eventHref} prefetch={true} />}
                    nativeButton={false}
                    className="py-5 text-data-card-xs font-bold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Details
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
      {!event.is_private ? (
        <RsvpDialog event={event} open={rsvpOpen} onOpenChange={setRsvpOpen} />
      ) : null}
    </article>
  );
}
